export const MOBILE_CHAT_VIEWPORT_EPSILON = 1;
export const MOBILE_CHAT_OPEN_STABLE_FRAMES = 2;
export const MOBILE_CHAT_OPENING_MAX_FRAMES = 36;
export const MOBILE_CHAT_RECOVERY_STABLE_FRAMES = 2;
export const MOBILE_CHAT_RECOVERY_MAX_FRAMES = 36;

function positiveNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
}

function finiteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function isAndroidRuntime() {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(String(navigator.userAgent || ''));
}

export function normalizeMobileChatViewportSnapshot(snapshot = {}) {
  const innerHeight = positiveNumber(snapshot.innerHeight);
  const clientHeight = positiveNumber(snapshot.clientHeight);
  const visualHeight = positiveNumber(snapshot.visualHeight) || innerHeight || clientHeight || 1;
  const offsetTop = Math.max(finiteNumber(snapshot.offsetTop), 0);
  const pageTop = Math.max(finiteNumber(snapshot.pageTop), 0);
  const visibleBottom = offsetTop + visualHeight;
  const layoutHeight = Math.max(innerHeight, clientHeight, visibleBottom, 1);

  return {
    innerHeight,
    clientHeight,
    visualHeight,
    offsetTop,
    pageTop,
    visibleBottom,
    layoutHeight,
  };
}

export function measureMobileChatStableClosedHeight({
  baselineHeight,
  snapshot,
  outerHeight,
  standalone: requestedStandalone = false,
}) {
  const viewport = normalizeMobileChatViewportSnapshot(snapshot);
  // iOS installed apps need outerHeight to recover WebKit's stale post-keyboard
  // viewport. Android Chrome PWA outerHeight may include system UI and would push
  // the composer below the visible Galaxy viewport, so it stays on layout height.
  const standalone = Boolean(requestedStandalone) && !isAndroidRuntime();
  return Math.max(
    positiveNumber(baselineHeight),
    viewport.layoutHeight,
    standalone ? positiveNumber(outerHeight) : 0,
    1,
  );
}

export function measureMobileChatKeyboardInset({ baselineHeight, snapshot }) {
  const viewport = normalizeMobileChatViewportSnapshot(snapshot);
  const baseline = positiveNumber(baselineHeight) || viewport.visualHeight || 1;
  const keyboardInset = Math.max(baseline - viewport.visualHeight, 0);

  return {
    ...viewport,
    baselineHeight: baseline,
    keyboardInset,
    viewportRecovered: keyboardInset <= MOBILE_CHAT_VIEWPORT_EPSILON,
  };
}

export function measureMobileChatVisualViewportGeometry(
  snapshot = {},
  { androidRuntime = isAndroidRuntime() } = {},
) {
  const viewport = normalizeMobileChatViewportSnapshot(snapshot);
  const geometry = {
    appliedTop: viewport.offsetTop,
    appliedHeight: viewport.visualHeight,
    visualViewportBottom: viewport.visibleBottom,
  };

  // Chrome on Android already pans the visual viewport around the focused field.
  // Applying offsetTop again to a fixed workspace double-counts that pan and can
  // push the composer below the keyboard, especially during mention interactions.
  // iOS keeps the original offset because WebKit's visual viewport trace needs it.
  if (androidRuntime) geometry.appliedTop = 0;
  return geometry;
}

export function hasMobileChatViewportHeightReboundedPastMidpoint({
  baselineHeight,
  minimumVisualHeight,
  currentVisualHeight,
}) {
  const baseline = positiveNumber(baselineHeight);
  const minimum = positiveNumber(minimumVisualHeight);
  const current = positiveNumber(currentVisualHeight);
  if (!baseline || !minimum || baseline <= minimum) return false;

  const midpoint = minimum + ((baseline - minimum) / 2);
  return current >= midpoint && current > minimum + MOBILE_CHAT_VIEWPORT_EPSILON;
}

export function nextMobileChatStableFrameCount({ previousCount, previousValue, currentValue }) {
  const previous = finiteNumber(previousValue);
  const current = finiteNumber(currentValue);
  if (Math.abs(current - previous) <= MOBILE_CHAT_VIEWPORT_EPSILON) {
    return Math.max(Number(previousCount) || 0, 0) + 1;
  }
  return 0;
}

export function shouldStartMobileChatRecovery({
  keyboardSeen,
  openSettled,
  focused,
  explicitRecovery,
  viewportRecovered,
  reboundedPastMidpoint,
}) {
  if (!keyboardSeen) return false;
  if (explicitRecovery || !focused) return true;
  if (!openSettled) return false;
  return viewportRecovered || reboundedPastMidpoint;
}

export function shouldFinalizeMobileChatRecovery({
  keyboardSeen,
  openSettled,
  viewportRecovered,
  reboundedPastMidpoint,
  stableFrameCount,
  recoveryFrameCount,
  focused,
}) {
  if (!keyboardSeen || !openSettled) return false;
  if (viewportRecovered) return true;
  if (!reboundedPastMidpoint || stableFrameCount < MOBILE_CHAT_RECOVERY_STABLE_FRAMES) return false;
  if (!focused) return true;
  return recoveryFrameCount >= MOBILE_CHAT_RECOVERY_MAX_FRAMES;
}
